/** SUNTRONIX ONE — Warehouse · Inward Receipt (mobile/desktop).
 *
 *  Expected-vs-Received scan flow driven by a reverse pickup manifest.
 *  Every scan is one of:
 *
 *    matched     → tick that expected serial + count towards received
 *    duplicate   → HARD BLOCK (Exception Engine fires
 *                  EXC-INWARD-DUPLICATE-SCAN) — cannot re-receive
 *    unexpected  → soft flag (EXC-INWARD-UNEXPECTED-SERIAL) — still
 *                  logged so it doesn't disappear into the warehouse
 *
 *  URL contract:
 *    /mobile/warehouse-inward
 *    /mobile/warehouse-inward?pickup=<PICKUP_CODE_OR_ID>
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useWedgeScanner } from "@/hooks/useWedgeScanner";
import {
  ArrowLeft, ScanLine, Check, AlertTriangle, XCircle, Loader2, Boxes,
  Package, ArrowRight,
} from "lucide-react";

export default function WarehouseInward() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [pickupCode, setPickupCode] = useState(sp.get("pickup") || "");
  const [state, setState] = useState(null);          // /inward-session state
  const [manual, setManual] = useState("");
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);          // {code, kind, message, at}
  const audioRef = useRef({
    ok: new Audio("data:audio/wav;base64,UklGRjIAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQ4AAACAgICAgICAgICAgICAgIA="),
    warn: null,
  });

  // ---- Session loader --------------------------------------------------
  const loadSession = useCallback(async (idOrCode) => {
    if (!idOrCode) return;
    setBusy(true);
    try {
      // Classify first so we can accept both raw ids and pickup codes.
      const cls = await api.post("/warehouse/classify", { code: idOrCode });
      if (cls.data.kind !== "pickup") {
        toast.error(`"${idOrCode}" is not a reverse pickup.`);
        setState(null);
        return;
      }
      const pid = cls.data.entity.id;
      const { data } = await api.get(`/warehouse/inward-session/${pid}`);
      setState(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load pickup");
      setState(null);
    } finally { setBusy(false); }
  }, []);

  useEffect(() => {
    if (pickupCode) loadSession(pickupCode);
  }, [pickupCode, loadSession]);

  // ---- Scan handler ----------------------------------------------------
  const scan = useCallback(async (code) => {
    if (!code || busy) return;
    if (!state) {
      toast.error("Scan the reverse pickup first.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/warehouse/inward-receive", {
        code, pickup_id: state.pickup_id,
      });
      const kind = data.kind || "matched";
      const message = data.message
        || (kind === "matched" ? `Received ${code}` : `${kind}: ${code}`);
      setRecent((r) => [{
        code, kind, message,
        at: new Date().toLocaleTimeString(),
      }, ...r].slice(0, 30));
      if (kind === "matched") toast.success(message);
      else toast.warning(message);
      // Refresh session state so the Expected list ticks in real-time.
      const { data: fresh } = await api.get(
        `/warehouse/inward-session/${state.pickup_id}`);
      setState(fresh);
    } catch (e) {
      const detail = e.response?.data?.detail || {};
      // Duplicate hard-blocks come back as 409 with kind=duplicate.
      const kind = detail.kind || "error";
      const message = detail.message
        || (typeof detail === "string" ? detail : "Scan failed");
      setRecent((r) => [{
        code, kind, message, at: new Date().toLocaleTimeString(),
      }, ...r].slice(0, 30));
      toast.error(message);
    } finally { setBusy(false); }
  }, [busy, state]);

  useWedgeScanner(scan, { enabled: Boolean(state) });

  // ---- Header / progress ----------------------------------------------
  const progressPct = useMemo(() => {
    if (!state?.expected_count) return 0;
    return Math.round((state.received_count / state.expected_count) * 100);
  }, [state]);

  return (
    <div className="max-w-2xl mx-auto space-y-5 pb-16"
         data-testid="warehouse-inward">
      <header className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/mobile/warehouse")}
          data-testid="inward-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> Warehouse
        </Button>
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
            Warehouse · Inward Receipt
          </div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight mt-0.5">
            Expected vs Received
          </h1>
        </div>
      </header>

      {/* Pickup selector */}
      {!state && (
        <div className="st-panel p-4 space-y-3" data-testid="inward-picker">
          <label className="block text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
            Scan or type the reverse pickup code
          </label>
          <div className="flex items-center gap-2">
            <ScanLine className="h-5 w-5 text-slate-500" />
            <Input
              value={pickupCode}
              onChange={(e) => setPickupCode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter"
                && loadSession(pickupCode.trim())}
              placeholder="e.g. PKP-2026-000123"
              className="h-10 text-[14px]"
              autoFocus
              data-testid="inward-pickup-input"
            />
            <Button onClick={() => loadSession(pickupCode.trim())}
              disabled={!pickupCode.trim() || busy}
              data-testid="inward-pickup-load">
              {busy
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <ArrowRight className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-[11px] text-slate-500">
            Once loaded, every serial you scan is checked against this
            pickup&apos;s manifest. Duplicates are hard-blocked; unexpected
            serials are flagged for reconciliation.
          </p>
        </div>
      )}

      {/* Live progress + scan input */}
      {state && (
        <>
          <div className="st-panel p-4 space-y-3" data-testid="inward-progress">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                  Pickup
                </div>
                <div className="font-mono text-[13px] text-slate-900">
                  {state.pickup_code || state.pickup_id}
                </div>
              </div>
              <button onClick={() => { setState(null); setRecent([]); setPickupCode(""); }}
                className="text-[11px] text-slate-500 hover:text-slate-700 underline"
                data-testid="inward-change-pickup">Change</button>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <Tile label="Expected" value={state.expected_count} tint="slate" />
              <Tile label="Received" value={state.received_count} tint="emerald" />
              <Tile label="Unexpected" value={(state.unexpected || []).length} tint="amber" />
            </div>
            <div className="h-1.5 rounded overflow-hidden bg-slate-100">
              <div className="h-full bg-emerald-500 transition-all"
                style={{ width: `${progressPct}%` }} />
            </div>
            <div className="text-[11px] text-slate-500 flex items-center justify-between">
              <span>{state.remaining_count} remaining</span>
              <span className="font-mono">{progressPct}%</span>
            </div>
          </div>

          <div className="st-panel p-4 space-y-3" data-testid="inward-scan">
            <div className="flex items-center gap-2">
              <ScanLine className="h-5 w-5 text-slate-500" />
              <Input
                value={manual}
                onChange={(e) => setManual(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && manual.trim()) {
                    scan(manual.trim()); setManual("");
                  }
                }}
                placeholder="Scan serial or asset tag…"
                className="h-10 text-[14px]"
                autoFocus
                data-testid="inward-scan-input"
              />
              <Button onClick={() => { scan(manual.trim()); setManual(""); }}
                disabled={!manual.trim() || busy}
                data-testid="inward-scan-go">
                {busy
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <ArrowRight className="h-4 w-4" />}
              </Button>
            </div>
            <div className="text-[11px] text-slate-500 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1"><Check className="h-3 w-3 text-emerald-600" /> matched</span>
              <span className="inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3 text-amber-600" /> unexpected</span>
              <span className="inline-flex items-center gap-1"><XCircle className="h-3 w-3 text-red-600" /> duplicate → blocked</span>
            </div>
          </div>

          {/* Expected list — each serial ticks green when received */}
          <div className="st-panel" data-testid="inward-expected">
            <div className="px-3 py-2 border-b border-slate-100 text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1">
              <Boxes className="h-3 w-3" /> Expected serials
            </div>
            <ul className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
              {(state.expected || []).map((a) => (
                <li key={a.id} className="px-3 py-2 flex items-center justify-between text-[12px]"
                  data-testid={`inward-expected-${a.id}`}>
                  <div>
                    <div className="font-mono text-slate-900">{a.serial || a.asset_tag}</div>
                    <div className="text-[10px] text-slate-500">
                      {[a.brand, a.model].filter(Boolean).join(" ") || a.category || "—"}
                    </div>
                  </div>
                  {a.received
                    ? <span className="inline-flex items-center gap-1 text-emerald-700">
                        <Check className="h-3.5 w-3.5" /> received
                      </span>
                    : <span className="text-slate-400 text-[11px]">pending</span>}
                </li>
              ))}
              {(state.expected || []).length === 0 && (
                <li className="px-3 py-4 text-[12px] text-slate-400 text-center">
                  Pickup has no expected serials on its manifest.
                </li>
              )}
            </ul>
          </div>

          {/* Unexpected & recent scans */}
          {recent.length > 0 && (
            <div className="st-panel" data-testid="inward-recent">
              <div className="px-3 py-2 border-b border-slate-100 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                Recent scans
              </div>
              <ul className="divide-y divide-slate-100 max-h-52 overflow-y-auto">
                {recent.map((r, i) => (
                  <li key={i} className="px-3 py-2 flex items-center justify-between text-[12px]">
                    <div className="flex items-center gap-2">
                      {r.kind === "matched"    && <Check className="h-3.5 w-3.5 text-emerald-600" />}
                      {r.kind === "unexpected" && <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />}
                      {r.kind === "duplicate"  && <XCircle className="h-3.5 w-3.5 text-red-600" />}
                      {r.kind === "error"      && <XCircle className="h-3.5 w-3.5 text-slate-500" />}
                      <div>
                        <div className="font-mono text-slate-900">{r.code}</div>
                        <div className="text-[10px] text-slate-500">{r.message}</div>
                      </div>
                    </div>
                    <div className="text-[10px] text-slate-400 tabular-nums">{r.at}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Tile({ label, value, tint }) {
  const cls = {
    slate:   "bg-slate-50 text-slate-800 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
  }[tint] || "bg-slate-50 text-slate-800 border-slate-200";
  return (
    <div className={`border rounded px-2 py-2 ${cls}`}
         data-testid={`inward-tile-${label.toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-xl font-heading font-semibold mt-0.5 tabular-nums">
        {value ?? 0}
      </div>
    </div>
  );
}
